// TimeByStatusCards.jsx
import React from 'react';
import { BadgeDelta, Card, Flex, Metric, Text, Title, LineChart } from '@tremor/react';
import { Tooltip } from '@mui/material';
import { months } from '../../../../utils/helper';
import { ConstructionOutlined } from '@mui/icons-material';

// Pequeno helper: formata valores que já vêm como "HH:MM:SS" ou números
const defaultFormatter = (v, unit) => {
  if (v == null) return '—';
  if (typeof v === 'string') return v; // já está em "HH:MM:SS"
  const num = Number(v);
  if (Number.isNaN(num)) return String(v);
  return `${Math.round(num)}${unit ? ` ${unit}` : ''}`;
};

// Converte "HH:MM:SS" -> segundos (number)
const parseDurationToSeconds = (value) => {
  if (value == null) return null;

  if (typeof value === 'number') return value;

  if (typeof value !== 'string') {
    const num = Number(value);
    return Number.isNaN(num) ? null : num;
  }

  const parts = value.split(':');
  if (parts.length !== 3) return null;

  const [hh, mm, ss] = parts.map(Number);
  if ([hh, mm, ss].some((n) => Number.isNaN(n))) return null;

  return hh * 3600 + mm * 60 + ss;
};

// Converte segundos -> "HH:MM:SS"
const formatSecondsAsHHMMSS = (seconds) => {
  if (seconds == null || Number.isNaN(seconds)) return '—';
  const total = Math.round(seconds);
  const hh = Math.floor(total / 3600);
  const mm = Math.floor((total % 3600) / 60);
  const ss = total % 60;

  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(hh)}:${pad(mm)}:${pad(ss)}`;
};

const secondsValueFormatter = (v) => formatSecondsAsHHMMSS(v);

// Mapeia chaves técnicas para rótulos amigáveis
const LABELS = {
  tempoConfirmacao: 'T.M Confirmação',
  tempoProducao: 'T.M Produção',
  tempoEntrega: 'T.M Entrega',
  tempoTotal: 'T.M Pedido',
};

export default function TimeByStatusCards({
  summary = {}, // objeto exatamente no formato enviado (tempoConfirmacao, tempoProducao, ...)
  valueFormatter = defaultFormatter,
  unit, // opcional: mantém compatibilidade com seu formatter
  tooltipMap = {}, // opcional: { tempoConfirmacao: '...', ... }
}) {
  const orderedKeys = ['tempoConfirmacao', 'tempoProducao', 'tempoEntrega', 'tempoTotal'];

  const cards = orderedKeys
    .filter((k) => summary && summary[k])
    .map((key) => {
      const node = summary[key] || {};
      const mesAtual = node.mesAtual || {};
      const mesAnterior = node.mesAnterior || {};
      const delta = node.delta;
      const outrosMeses = node.outrosMeses || [];

      // Decide o tipo do delta no Tremor:
      let deltaType;
      if (typeof delta === 'number') {
        deltaType = delta > 0 ? 'decrease' : delta < 0 ? 'increase' : 'unchanged';
      } else {
        deltaType = undefined; // não renderiza badge se não houver delta
      }

      return {
        id: key,
        caption: LABELS[key] || key,
        tooltip: tooltipMap[key],
        value: mesAtual.media ?? '—',
        previous: mesAnterior.media ?? '—',
        min: mesAtual.minimo ?? null,
        max: mesAtual.maximo ?? null,
        delta,
        deltaType,
        outrosMeses,
      };
    });

  return (
    <dl className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
      {cards.map((item) => {
        const {
          id,
          caption,
          value,
          previous,
          min,
          max,
          tooltip,
          delta,
          deltaType,
          outrosMeses,
        } = item;

        // Monta dados do gráfico a partir de outrosMeses (usando tempo médio)
        const chartData =
          (outrosMeses || [])
            .filter((o) => o && (o.media != null || o.minimo != null || o.maximo != null))
            .map((o) => {
              const segundosMedio = parseDurationToSeconds(o.media);
              const segundosMin = parseDurationToSeconds(o.minimo);
              const segundosMax = parseDurationToSeconds(o.maximo);

              // Se tudo vier nulo, ignora o ponto
              if (segundosMedio == null && segundosMin == null && segundosMax == null) {
                return null;
              }

              const mesNumero = Number(o.mes);
              const ano = Number(o.ano);

              // proteção básica
              if (!mesNumero || mesNumero < 1 || mesNumero > 12 || !ano) {
                return null;
              }

              const monthLabel = months[mesNumero - 1];

              const anoStr = String(ano);
              // const yearLabel = /^\d{2,4}$/.test(anoStr) ? anoStr.slice(-2) : "";

              // const label = yearLabel ? `${monthLabel}/${yearLabel}` : monthLabel;
              const label = monthLabel;

              return {
                mesNumero,
                ano, // <-- IMPORTANTE: guardar o ano para ordenar
                mes: label,
                "Tempo mínimo": segundosMin,
                "Tempo médio": segundosMedio,
                "Tempo máximo": segundosMax,
              };
            })
            .filter(Boolean)

            // ORDENAÇÃO CRONOLÓGICA CORRETA (atravessa anos sem quebrar)
            .sort((a, b) => (a.ano * 12 + (a.mesNumero - 1)) - (b.ano * 12 + (b.mesNumero - 1)));

        const hasChart = chartData.length > 0;


        return (
          <Card key={id}>
            <Flex alignItems="start" justifyContent="between">
              <div>
                {tooltip ? (
                  <Tooltip title={tooltip}>
                    <Title>{caption}</Title>
                  </Tooltip>
                ) : (
                  <Title>{caption}</Title>
                )}

                <dd className="mt-1 flex items-baseline gap-2.5">
                  <Metric>{valueFormatter(value, unit)}</Metric>
                  <Text className="text-tremor-default text-tremor-content dark:text-dark-tremor-content">
                    mês ant. {valueFormatter(previous, unit)}
                  </Text>
                </dd>
              </div>

              {typeof delta === 'number' && (
                <BadgeDelta deltaType={deltaType}>
                  {`${delta > 0 ? '+' : ''}${delta.toFixed(1)}%`}
                </BadgeDelta>
              )}
            </Flex>

            {/* Linha com Min e Max (mês atual) */}
            <dd className="mt-2 grid grid-cols-2 gap-4">
              <div>
                <p className="text-tremor-default text-tremor-content dark:text-dark-tremor-content">
                  T. Mínimo
                </p>
                <p className="mt-0 text-tremor-default font-semibold text-tremor-content-strong dark:text-dark-tremor-content-strong">
                  {valueFormatter(min, unit)}
                </p>
              </div>
              <div>
                <p className="text-tremor-default text-tremor-content dark:text-dark-tremor-content">
                  T. Máximo
                </p>
                <p className="mt-0 text-tremor-default font-semibold text-tremor-content-strong dark:text-dark-tremor-content-strong">
                  {valueFormatter(max, unit)}
                </p>
              </div>
            </dd>

            {/* Histórico já sempre visível (outros meses) */}
            {hasChart && (
              <div className="mt-3 border-t pt-3">
                <Text className="mb-2 text-xs text-tremor-content dark:text-dark-tremor-content">
                  Histórico (outros meses – tempo médio)
                </Text>

                <LineChart
                  data={chartData}
                  index="mes"
                  // categories={["Tempo mínimo", "Tempo médio", "Tempo máximo"]}
                  categories={["Tempo médio"]}
                  valueFormatter={secondsValueFormatter}
                  showLegend={true}
                  yAxisWidth={64}
                  curveType="monotone"
                />
              </div>
            )}
          </Card>
        );
      })}
    </dl>
  );
}
